/**
 * Permission System Types
 *
 * Defines the permission model for controlling access to features based on user roles and teams.
 * - Owners always have all permissions (hardcoded)
 * - Admins have configurable permissions (defaults to most permissions)
 * - Team members (users) have team-based permissions that can be configured per team
 * - Per-user overrides can extend or restrict role/team-based permissions
 */

import { UserMembershipTypeModel } from './user';
import { TeamKind, TeamPermissionTemplate, TeamSummary } from './team';

/**
 * All available permission keys in the system.
 * Organized by feature area for clarity.
 */
export type PermissionKey =
  // Dashboard
  | 'dashboard.view'
  | 'dashboard.viewAllStats'
  // Projects
  | 'projects.view'
  | 'projects.viewAll'
  | 'projects.create'
  | 'projects.edit'
  | 'projects.delete'
  | 'projects.updateProgress'
  // Customers
  | 'customers.view'
  | 'customers.create'
  | 'customers.edit'
  // Quotes
  | 'quotes.view'
  | 'quotes.viewAll'
  | 'quotes.create'
  | 'quotes.edit'
  | 'quotes.manageTemplates'
  // Invoices
  | 'invoices.view'
  | 'invoices.create'
  | 'invoices.edit'
  | 'invoices.book'
  | 'invoices.manageTemplates'
  // Materials
  | 'materials.view'
  | 'materials.create'
  | 'materials.edit'
  // Creditor invoices (supplier invoices)
  | 'creditorInvoices.view'
  | 'creditorInvoices.book'
  // Timeplans (project phase timelines)
  | 'timeplans.view'
  | 'timeplans.manage'
  // Activity feed (audit-log read API)
  | 'activity.viewAll'
  // Planning
  | 'planning.view'
  | 'planning.viewAll'
  | 'planning.edit'
  // Users
  | 'users.view'
  | 'users.create'
  | 'users.edit'
  | 'users.viewSelf'
  // Settings
  | 'settings.view'
  | 'settings.editCompany'
  | 'settings.manageMembers'
  | 'settings.managePermissions'
  | 'settings.dangerZone'
  // Reports & Insights
  | 'reports.view'
  | 'insights.view'
  // Time Entries
  | 'timeEntries.view'
  | 'timeEntries.create'
  | 'timeEntries.viewAll'
  | 'timeEntries.approve'
  | 'timeEntries.editOthers'
  // Budgettering
  | 'budgettering.view'
  | 'budgettering.viewAllBudgets'
  | 'budgettering.viewTimeOverview'
  | 'budgettering.edit'
  | 'budgettering.recordStade'
  // Quality (KS/KLS)
  | 'quality.view'
  // KLS (Quality Management)
  | 'kls.view'
  | 'kls.createInspections'
  | 'kls.manageChecklists'
  // Bonus
  | 'bonus.view'
  | 'bonus.viewAll'
  | 'bonus.manage'
  // Workspace (Bid Workspace)
  | 'workspace.create'
  | 'workspace.manageParticipants'
  | 'workspace.decideOutcome'
  | 'workspace.overrideBillable'
  // Price Library & Calculations
  | 'priceLibrary.view'
  | 'priceLibrary.edit'
  | 'calculations.view'
  | 'calculations.edit'
  | 'calculations.generateQuote'
  // Partners (External Collaboration)
  | 'partners.invite'
  | 'partners.view'
  | 'partners.manage'
  // Defects (Fejl og mangler)
  | 'defects.view'
  | 'defects.create'
  | 'defects.edit'
  | 'defects.delete'
  // Aftalesedler (Project supplements / change orders)
  | 'aftalesedler.create'
  | 'aftalesedler.view'
  | 'aftalesedler.edit'
  | 'aftalesedler.approve'
  // Tilbudslister (Reusable quote line-item templates)
  | 'tilbudslister.view'
  | 'tilbudslister.manage'
  // Integrations
  | 'integrations.economic.use'
  | 'integrations.dinero.use'
  | 'integrations.payroll.manage'
  | 'integrations.connections.manage'
  // Assets
  | 'assets.view'
  | 'assets.edit'
  // Ejendomme (properties). viewAccessCodes gates the nøgleboks/port/alarm codes; it is
  // deliberately granted as broadly as `view` — the technician on site is who needs them.
  | 'properties.view'
  | 'properties.edit'
  | 'properties.viewAccessCodes'
  // KS Templates (Kvalitetssikring / quality-assurance checklist templates)
  | 'ksTemplates.view'
  | 'ksTemplates.edit'
  // Verification Templates (Installationseftervisning checklist templates)
  | 'verificationTemplates.view'
  | 'verificationTemplates.edit'
  // KS Project Items (Kvalitetssikring checklist-item instances applied to a project)
  | 'ksProjectItems.view'
  | 'ksProjectItems.edit'
  // KLS Checklists (KLS tjeklister)
  | 'klsChecklists.view'
  | 'klsChecklists.edit'
  // KLS Equipment (KLS måleudstyr)
  | 'klsEquipment.view'
  | 'klsEquipment.edit'
  // KLS Evaluations (KLS evalueringer)
  | 'klsEvaluations.view'
  | 'klsEvaluations.edit'
  // KLS Inspections (KLS inspektioner)
  | 'klsInspections.view'
  | 'klsInspections.edit'
  // KLS Competencies (KLS kompetencer)
  | 'klsCompetencies.view'
  | 'klsCompetencies.edit'
  // KLS Deviations (KLS afvigelser)
  | 'klsDeviations.view'
  | 'klsDeviations.edit'
  // Documents (generic arbitrary-MIME attachments)
  | 'documents.view'
  | 'documents.upload'
  | 'documents.edit'
  | 'documents.delete';

/**
 * Permission keys for the supplier organisation's own users (account managers and their admin).
 * Deliberately a separate union from {@link PermissionKey}: these are meaningful only in a
 * Supplier-plan org, so they never appear in the customer permission matrix, ALL_PERMISSIONS or
 * the customer role defaults, which stay complete over {@link PermissionKey}.
 */
export type SupplierPermissionKey =
  | 'supplier.customers.view'
  | 'supplier.customers.viewAll'
  | 'supplier.customers.create'
  | 'supplier.customers.manage'
  | 'supplier.kpis.view'
  | 'supplier.kpis.manage'
  | 'supplier.catalog.manage'
  | 'supplier.referrals.view';

/** Every {@link SupplierPermissionKey}. Mirrors `Permissions.Supplier` in the backend. */
export const SUPPLIER_PERMISSION_KEYS: readonly SupplierPermissionKey[] = [
  'supplier.customers.view',
  'supplier.customers.viewAll',
  'supplier.customers.create',
  'supplier.customers.manage',
  'supplier.kpis.view',
  'supplier.kpis.manage',
  'supplier.catalog.manage',
  'supplier.referrals.view',
];


/**
 * Metadata for displaying permissions in the UI.
 * All labels and descriptions are in Danish.
 */
export interface PermissionMetadata {
  key: PermissionKey;
  label: string;
  description: string;
  category: PermissionCategory;
}

/**
 * Permission categories for grouping in the UI.
 */
export type PermissionCategory =
  | 'dashboard'
  | 'projects'
  | 'customers'
  | 'quotes'
  | 'invoices'
  | 'materials'
  | 'creditorInvoices'
  | 'timeplans'
  | 'activity'
  | 'planning'
  | 'users'
  | 'settings'
  | 'reports'
  | 'timeEntries'
  | 'budgettering'
  | 'quality'
  | 'kls'
  | 'bonus'
  | 'priceLibrary'
  | 'partners'
  | 'defects'
  | 'aftalesedler'
  | 'tilbudslister'
  | 'integrations'
  | 'assets'
  | 'properties'
  | 'ksTemplates'
  | 'verificationTemplates'
  | 'ksProjectItems'
  | 'klsChecklists'
  | 'klsEquipment'
  | 'klsEvaluations'
  | 'klsInspections'
  | 'klsCompetencies'
  | 'klsDeviations'
  | 'documents';

/**
 * Category metadata for UI grouping.
 */
export interface PermissionCategoryMetadata {
  key: PermissionCategory;
  label: string;
  description: string;
}

/**
 * All permission categories with Danish labels.
 */
export const PERMISSION_CATEGORIES: PermissionCategoryMetadata[] = [
  { key: 'dashboard', label: 'Dashboard', description: 'Oversigt og statistik' },
  { key: 'projects', label: 'Projekter', description: 'Sager og projektstyring' },
  { key: 'customers', label: 'Kunder', description: 'Kundekartotek' },
  { key: 'quotes', label: 'Tilbud', description: 'Tilbud og arbejdsrum' },
  { key: 'invoices', label: 'Fakturaer', description: 'Fakturering' },
  { key: 'materials', label: 'Materialer', description: 'Materialeforbrug og leverandørfakturaer' },
  { key: 'creditorInvoices', label: 'Leverandørfakturaer', description: 'Indkomne fakturaer fra leverandører' },
  { key: 'timeplans', label: 'Tidsplaner', description: 'Projektfasetidsplaner' },
  { key: 'activity', label: 'Aktivitet', description: 'Aktivitetsfeed (revisionslog)' },
  { key: 'planning', label: 'Planlægning', description: 'Ressourceplanlægning' },
  { key: 'users', label: 'Brugere', description: 'Medarbejdere og adgang' },
  { key: 'settings', label: 'Indstillinger', description: 'Virksomhedsindstillinger' },
  { key: 'reports', label: 'Rapporter', description: 'Rapporter og indsigter' },
  { key: 'timeEntries', label: 'Tidsregistrering', description: 'Tidsregistrering' },
  { key: 'budgettering', label: 'Budgettering', description: 'Budget- og tidsoversigt' },
  { key: 'quality', label: 'Kvalitet', description: 'Kvalitetssikring og kontrol' },
  { key: 'kls', label: 'KLS', description: 'Kvalitetsledelsessystem' },
  { key: 'bonus', label: 'Bonus', description: 'Bonusordning og udbetalinger' },
  { key: 'priceLibrary', label: 'Prisbibliotek & Kalkulation', description: 'Prisbibliotek og sagskalkulation' },
  { key: 'partners', label: 'Partnere', description: 'Ekstern samarbejde' },
  { key: 'defects', label: 'Fejl og mangler', description: 'Registrering og håndtering af fejl og mangler' },
  { key: 'aftalesedler', label: 'Aftalesedler', description: 'Tillægssedler og underskrifter på projekter' },
  { key: 'tilbudslister', label: 'Tilbudslister', description: 'Genbrugelige linjeskabeloner til tilbud' },
  { key: 'integrations', label: 'Integrationer', description: 'Tredjepartsintegrationer (e-conomic m.fl.)' },
  { key: 'assets', label: 'Aktiver', description: 'Kunderegister over installerede aktiver' },
  { key: 'properties', label: 'Ejendomme', description: 'Bygninger, enheder og adgangsforhold' },
  { key: 'ksTemplates', label: 'KS-skabeloner', description: 'Kvalitetssikring tjekliste-skabeloner' },
  { key: 'verificationTemplates', label: 'Installationseftervisningsskabeloner', description: 'Skabeloner til installationseftervisnings-tjeklister' },
  { key: 'ksProjectItems', label: 'KS-tjekpunkter', description: 'Kvalitetssikring tjekpunkter på projekter' },
  { key: 'klsChecklists', label: 'KLS tjeklister', description: 'KLS kvalitetsledelsessystem tjeklister' },
  { key: 'klsEquipment', label: 'KLS måleudstyr', description: 'KLS måleinstrumenter og kalibreringsstyring' },
  { key: 'klsEvaluations', label: 'KLS evalueringer', description: 'KLS kvalitetsledelsessystem selvevalueringer' },
  { key: 'klsInspections', label: 'KLS inspektioner', description: 'KLS inspektioner udført på projekter og opgaver' },
  { key: 'klsCompetencies', label: 'KLS kompetencer', description: 'KLS kompetence- og certifikatprofiler for medarbejdere' },
  { key: 'klsDeviations', label: 'KLS afvigelser', description: 'KLS registrering og håndtering af afvigelser og non-conformancer' },
  { key: 'documents', label: 'Dokumenter', description: 'Vilkårlige MIME-filvedhæftninger (PDF, Office osv.)' },
];

/**
 * All permissions with metadata for UI display.
 */
export const ALL_PERMISSIONS: PermissionMetadata[] = [
  // Dashboard
  { key: 'dashboard.view', label: 'Se dashboard', description: 'Adgang til dashboardet', category: 'dashboard' },
  { key: 'dashboard.viewAllStats', label: 'Se alle statistikker', description: 'Se alle virksomhedsstatistikker, ikke kun egne', category: 'dashboard' },

  // Projects
  { key: 'projects.view', label: 'Se projekter', description: 'Adgang til projekter (kun tildelte for brugere)', category: 'projects' },
  { key: 'projects.viewAll', label: 'Se alle projekter', description: 'Se alle projekter, ikke kun tildelte', category: 'projects' },
  { key: 'projects.create', label: 'Opret projekter', description: 'Opret nye projekter', category: 'projects' },
  { key: 'projects.edit', label: 'Rediger projekter', description: 'Rediger eksisterende projekter', category: 'projects' },
  { key: 'projects.delete', label: 'Slet projekter', description: 'Slet projekter', category: 'projects' },
  { key: 'projects.updateProgress', label: 'Opdater fremskridt', description: 'Opdater projekt fremskridt (forventet/faktisk)', category: 'projects' },

  // Customers
  { key: 'customers.view', label: 'Se kunder', description: 'Adgang til kundekartotek', category: 'customers' },
  { key: 'customers.create', label: 'Opret kunder', description: 'Opret nye kunder', category: 'customers' },
  { key: 'customers.edit', label: 'Rediger kunder', description: 'Rediger kundeoplysninger', category: 'customers' },

  // Quotes (including workspace/arbejdsrum)
  { key: 'quotes.view', label: 'Se tilbud', description: 'Adgang til tilbud', category: 'quotes' },
  { key: 'quotes.viewAll', label: 'Se alle tilbud', description: 'Se alle tilbud, ikke kun egne', category: 'quotes' },
  { key: 'quotes.create', label: 'Opret tilbud', description: 'Opret nye tilbud', category: 'quotes' },
  { key: 'quotes.edit', label: 'Rediger tilbud', description: 'Rediger eksisterende tilbud', category: 'quotes' },
  { key: 'quotes.manageTemplates', label: 'Administrer tilbudsskabeloner', description: 'Opret og rediger genbrugelige tilbudsskabeloner', category: 'quotes' },
  { key: 'workspace.create', label: 'Aktivér arbejdsrum', description: 'Aktivér samarbejdsrum på tilbud', category: 'quotes' },
  { key: 'workspace.manageParticipants', label: 'Administrer deltagere', description: 'Tilføj og fjern deltagere i arbejdsrum', category: 'quotes' },
  { key: 'workspace.decideOutcome', label: 'Afgør udfald', description: 'Markér tilbud som vundet eller tabt', category: 'quotes' },
  { key: 'workspace.overrideBillable', label: 'Ændr fakturerbar status', description: 'Ændr om timer er fakturerbare', category: 'quotes' },

  // Invoices
  { key: 'invoices.view', label: 'Se fakturaer', description: 'Adgang til fakturaer', category: 'invoices' },
  { key: 'invoices.create', label: 'Opret fakturaer', description: 'Opret nye fakturaer', category: 'invoices' },
  { key: 'invoices.edit', label: 'Rediger fakturaer', description: 'Rediger eksisterende kladdefakturaer', category: 'invoices' },
  { key: 'invoices.book', label: 'Bogfør fakturaer', description: 'Bogfør og send fakturaer; udsted kreditnotaer', category: 'invoices' },
  { key: 'invoices.manageTemplates', label: 'Administrer tilbudslister', description: 'Opret og rediger genbrugelige tilbudslister (linjeskabeloner)', category: 'invoices' },

  // Materials (project consumption + creditor invoices)
  { key: 'materials.view', label: 'Se materialer', description: 'Adgang til materialeposter på projekter', category: 'materials' },
  { key: 'materials.create', label: 'Opret materialer', description: 'Tilføj materialeposter til projekter', category: 'materials' },
  { key: 'materials.edit', label: 'Rediger materialer', description: 'Rediger og flyt materialeposter mellem projekter', category: 'materials' },

  // Creditor invoices
  { key: 'creditorInvoices.view', label: 'Se leverandørfakturaer', description: 'Adgang til indkomne leverandørfakturaer', category: 'creditorInvoices' },
  { key: 'creditorInvoices.book', label: 'Bogfør leverandørfakturaer', description: 'Allokér og bogfør leverandørfakturaer på projekter', category: 'creditorInvoices' },


  // Timeplans
  { key: 'timeplans.view', label: 'Se tidsplaner', description: 'Adgang til projektets tidsplan', category: 'timeplans' },
  { key: 'timeplans.manage', label: 'Administrer tidsplaner', description: 'Opret og rediger tidsplanens faser', category: 'timeplans' },

  // Activity feed
  { key: 'activity.viewAll', label: 'Se aktivitetsfeed', description: 'Se det fulde aktivitetsfeed på tværs af entiteter (revisionsdata)', category: 'activity' },

  // Planning
  { key: 'planning.view', label: 'Se planlægning', description: 'Adgang til ressourceplanlægning (kun egen)', category: 'planning' },
  { key: 'planning.viewAll', label: 'Se alle medarbejdere', description: 'Se og planlæg for alle medarbejdere, ikke kun egen', category: 'planning' },
  { key: 'planning.edit', label: 'Rediger planlægning', description: 'Rediger ressourceplanlægning', category: 'planning' },

  // Users
  { key: 'users.view', label: 'Se brugere', description: 'Adgang til brugerliste', category: 'users' },
  { key: 'users.create', label: 'Opret brugere', description: 'Inviter nye medarbejdere', category: 'users' },
  { key: 'users.edit', label: 'Rediger brugere', description: 'Rediger brugeroplysninger', category: 'users' },
  { key: 'users.viewSelf', label: 'Se egen bruger', description: 'Alle autentificerede brugere skal kunne hente egne brugeroplysninger', category: 'users' },

  // Settings
  { key: 'settings.view', label: 'Se indstillinger', description: 'Adgang til indstillinger', category: 'settings' },
  { key: 'settings.editCompany', label: 'Rediger virksomhed', description: 'Rediger virksomhedsoplysninger', category: 'settings' },
  { key: 'settings.manageMembers', label: 'Administrer medlemmer', description: 'Se og administrer virksomhedsmedlemmer', category: 'settings' },
  { key: 'settings.managePermissions', label: 'Administrer tilladelser', description: 'Administrer brugerroller og tilladelser', category: 'settings' },
  { key: 'settings.dangerZone', label: 'Farezone', description: 'Destruktive admin-handlinger (arkivér virksomhed, fremtidige hard-delete-flows)', category: 'settings' },

  // Reports & Insights
  { key: 'reports.view', label: 'Se rapporter', description: 'Adgang til rapporter', category: 'reports' },
  { key: 'insights.view', label: 'Se indsigter', description: 'Adgang til indsigter og analyser', category: 'reports' },

  // Time Entries
  { key: 'timeEntries.view', label: 'Se tidsregistreringer', description: 'Se egne tidsregistreringer', category: 'timeEntries' },
  { key: 'timeEntries.create', label: 'Opret tidsregistreringer', description: 'Registrer egen tid', category: 'timeEntries' },
  { key: 'timeEntries.viewAll', label: 'Se alle tidsregistreringer', description: 'Se alle medarbejderes tidsregistreringer', category: 'timeEntries' },
  { key: 'timeEntries.approve', label: 'Godkend tidsregistreringer', description: 'Godkend og afvis tidsregistreringer', category: 'timeEntries' },
  { key: 'timeEntries.editOthers', label: 'Redigér andres timer', description: 'Opret, ret og slet timer for andre medarbejdere (godkendere har det altid). Indsendte og godkendte timer kræver godkendelsesret, og fakturerede timer kan ikke ændres', category: 'timeEntries' },

  // Budgettering
  { key: 'budgettering.view', label: 'Se budgetter', description: 'Adgang til budgetoversigt', category: 'budgettering' },
  { key: 'budgettering.viewAllBudgets', label: 'Se alle budgetter', description: 'Se alle projektbudgetter', category: 'budgettering' },
  { key: 'budgettering.viewTimeOverview', label: 'Se tidsoversigt', description: 'Adgang til tidsoversigt i budgettering', category: 'budgettering' },
  { key: 'budgettering.edit', label: 'Rediger sagsbudget', description: 'Redigér budgettet på en sag og opret det fra kalkulation eller standardfordeling', category: 'budgettering' },
  { key: 'budgettering.recordStade', label: 'Registrér stade', description: 'Registrér hvor langt en sag er (faktisk stade)', category: 'budgettering' },

  // Quality
  { key: 'quality.view', label: 'Se Kvalitet', description: 'Adgang til KS og KLS', category: 'quality' },

  // KLS
  { key: 'kls.view', label: 'Se KLS', description: 'Adgang til kvalitetsledelsessystem', category: 'kls' },
  { key: 'kls.createInspections', label: 'Opret eftersyn', description: 'Opret og udfør eftersyn', category: 'kls' },
  { key: 'kls.manageChecklists', label: 'Administrer tjeklister', description: 'Opret og rediger KLS-tjeklister', category: 'kls' },

  // Bonus
  { key: 'bonus.view', label: 'Se egen bonus', description: 'Se egen bonusoversigt', category: 'bonus' },
  { key: 'bonus.viewAll', label: 'Se alle bonus', description: 'Se bonusoversigt for alle medarbejdere', category: 'bonus' },
  { key: 'bonus.manage', label: 'Administrer bonus', description: 'Konfigurer bonusordning', category: 'bonus' },

  // Price Library & Calculations
  { key: 'priceLibrary.view', label: 'Se prisbibliotek', description: 'Adgang til prisbiblioteket', category: 'priceLibrary' },
  { key: 'priceLibrary.edit', label: 'Rediger prisbibliotek', description: 'Opret, rediger og slet ydelser i prisbiblioteket', category: 'priceLibrary' },
  { key: 'calculations.view', label: 'Se kalkulationer', description: 'Se projektkalkulationer', category: 'priceLibrary' },
  { key: 'calculations.edit', label: 'Rediger kalkulationer', description: 'Opret og rediger projektkalkulationer', category: 'priceLibrary' },
  { key: 'calculations.generateQuote', label: 'Generer tilbud', description: 'Generer tilbud fra kalkulationer', category: 'priceLibrary' },

  // Partners (External Collaboration)
  { key: 'partners.invite', label: 'Inviter partnere', description: 'Inviter eksterne partnere til projekter og tilbud', category: 'partners' },
  { key: 'partners.view', label: 'Se partnere', description: 'Se liste over partnerinvitationer', category: 'partners' },
  { key: 'partners.manage', label: 'Administrer partnere', description: 'Fjern og administrer partneradgang', category: 'partners' },

  // Defects (Fejl og mangler)
  { key: 'defects.view', label: 'Se fejl og mangler', description: 'Adgang til at se fejl og mangler på projekter', category: 'defects' },
  { key: 'defects.create', label: 'Opret fejl og mangler', description: 'Opret nye fejl og mangler på projekter', category: 'defects' },
  { key: 'defects.edit', label: 'Rediger fejl og mangler', description: 'Rediger eksisterende fejl og mangler', category: 'defects' },
  { key: 'defects.delete', label: 'Slet fejl og mangler', description: 'Slet fejl og mangler', category: 'defects' },

  // Aftalesedler (Project supplements / change orders)
  { key: 'aftalesedler.create', label: 'Opret aftalesedler', description: 'Opret nye tillægssedler på projekter', category: 'aftalesedler' },
  { key: 'aftalesedler.view', label: 'Se aftalesedler', description: 'Adgang til at se aftalesedler på projekter', category: 'aftalesedler' },
  { key: 'aftalesedler.edit', label: 'Rediger aftalesedler', description: 'Rediger aftalesedler og administrer linjer og underskrifter', category: 'aftalesedler' },
  { key: 'aftalesedler.approve', label: 'Godkend aftalesedler', description: 'Godkend eller afvis aftalesedler (tæller med i kontraktsummen)', category: 'aftalesedler' },

  // Tilbudslister (Reusable quote line-item templates)
  { key: 'tilbudslister.view', label: 'Se tilbudslister', description: 'Adgang til at se genbrugelige tilbudslister', category: 'tilbudslister' },
  { key: 'tilbudslister.manage', label: 'Administrer tilbudslister', description: 'Opret, rediger og deaktiver tilbudslister', category: 'tilbudslister' },

  // Integrations
  { key: 'integrations.economic.use', label: 'Bruge e-conomic-integration', description: 'Tilladelse til at læse og skrive til e-conomic via Mesterplan', category: 'integrations' },
  { key: 'integrations.dinero.use', label: 'Bruge Dinero-integration', description: 'Tilladelse til at læse og skrive til Dinero via Mesterplan', category: 'integrations' },
  { key: 'integrations.payroll.manage', label: 'Administrer lønintegration', description: 'Tilladelse til at forbinde og administrere Danløn-integrationen via Mesterplan', category: 'integrations' },
  { key: 'integrations.connections.manage', label: 'Administrer produktforbindelser', description: 'Tilladelse til at forbinde og afbryde andre Rasmussen Solutions-produkter (f.eks. Trackrs) — kun admin/ejer', category: 'integrations' },

  // Assets
  { key: 'assets.view', label: 'Se aktiver', description: 'Adgang til at se kundeaktiver og udstyr', category: 'assets' },
  { key: 'assets.edit', label: 'Rediger aktiver', description: 'Opret, rediger og slet kundeaktiver', category: 'assets' },

  // Ejendomme
  { key: 'properties.view', label: 'Se ejendomme', description: 'Adgang til ejendomme og deres enheder', category: 'properties' },
  { key: 'properties.edit', label: 'Rediger ejendomme', description: 'Opret, rediger og slet ejendomme og enheder', category: 'properties' },
  { key: 'properties.viewAccessCodes', label: 'Se adgangskoder', description: 'Se nøgleboks-, port- og alarmkode samt adgangsbeskrivelse', category: 'properties' },

  // KS Templates
  { key: 'ksTemplates.view', label: 'Se KS-skabeloner', description: 'Adgang til at se kvalitetssikring skabeloner', category: 'ksTemplates' },
  { key: 'ksTemplates.edit', label: 'Rediger KS-skabeloner', description: 'Opret, rediger og slet KS-skabeloner', category: 'ksTemplates' },

  // Verification Templates
  { key: 'verificationTemplates.view', label: 'Se eftervisningsskabeloner', description: 'Adgang til at se installationseftervisnings-skabeloner', category: 'verificationTemplates' },
  { key: 'verificationTemplates.edit', label: 'Rediger eftervisningsskabeloner', description: 'Opret, rediger og slet installationseftervisnings-skabeloner', category: 'verificationTemplates' },

  // KS Project Items
  { key: 'ksProjectItems.view', label: 'Se KS-tjekpunkter', description: 'Adgang til at se kvalitetssikring tjekpunkter på projekter', category: 'ksProjectItems' },
  { key: 'ksProjectItems.edit', label: 'Rediger KS-tjekpunkter', description: 'Udfyld, tilsidesæt og administrer KS-tjekpunkter på projekter', category: 'ksProjectItems' },

  // KLS Checklists
  { key: 'klsChecklists.view', label: 'Se KLS tjeklister', description: 'Adgang til at se KLS tjeklister', category: 'klsChecklists' },
  { key: 'klsChecklists.edit', label: 'Rediger KLS tjeklister', description: 'Opret, rediger og slet KLS tjeklister', category: 'klsChecklists' },

  // KLS Equipment
  { key: 'klsEquipment.view', label: 'Se KLS måleudstyr', description: 'Adgang til at se KLS måleinstrumenter og kalibreringsstatus', category: 'klsEquipment' },
  { key: 'klsEquipment.edit', label: 'Rediger KLS måleudstyr', description: 'Opret, rediger og slet KLS måleinstrumenter', category: 'klsEquipment' },

  // KLS Evaluations
  { key: 'klsEvaluations.view', label: 'Se KLS evalueringer', description: 'Adgang til at se KLS kvalitetsledelsessystem selvevalueringer', category: 'klsEvaluations' },
  { key: 'klsEvaluations.edit', label: 'Rediger KLS evalueringer', description: 'Opret, rediger og slet KLS selvevalueringer', category: 'klsEvaluations' },

  // KLS Inspections
  { key: 'klsInspections.view', label: 'Se KLS inspektioner', description: 'Adgang til at se KLS inspektioner udført på projekter og opgaver', category: 'klsInspections' },
  { key: 'klsInspections.edit', label: 'Rediger KLS inspektioner', description: 'Opret, rediger og slet KLS inspektioner', category: 'klsInspections' },

  // KLS Competencies
  { key: 'klsCompetencies.view', label: 'Se KLS kompetencer', description: 'Adgang til at se KLS kompetence- og certifikatprofiler for medarbejdere', category: 'klsCompetencies' },
  { key: 'klsCompetencies.edit', label: 'Rediger KLS kompetencer', description: 'Opret, rediger og slet KLS kompetence- og certifikatprofiler', category: 'klsCompetencies' },

  // KLS Deviations
  { key: 'klsDeviations.view', label: 'Se KLS afvigelser', description: 'Adgang til at se KLS afvigelser og non-conformancer', category: 'klsDeviations' },
  { key: 'klsDeviations.edit', label: 'Rediger KLS afvigelser', description: 'Opret, rediger og slet KLS afvigelser', category: 'klsDeviations' },


  // Documents
  { key: 'documents.view', label: 'Se dokumenter', description: 'Adgang til at se vedhæftede dokumenter og filer', category: 'documents' },
  { key: 'documents.upload', label: 'Upload dokumenter', description: 'Upload nye dokumenter og filer', category: 'documents' },
  { key: 'documents.edit', label: 'Rediger dokumenter', description: 'Rediger metadata (billedtekst, tags, type) på vedhæftede dokumenter', category: 'documents' },
  { key: 'documents.delete', label: 'Slet dokumenter', description: 'Slet vedhæftede dokumenter og filer', category: 'documents' },
];

/**
 * Default permissions for admins.
 * Admins have full access except managing permissions (owner only by default).
 */
export const DEFAULT_ADMIN_PERMISSIONS: Record<PermissionKey, boolean> = {
  // Dashboard - full access
  'dashboard.view': true,
  'dashboard.viewAllStats': true,
  // Projects - full access
  'projects.view': true,
  'projects.viewAll': true,
  'projects.create': true,
  'projects.edit': true,
  'projects.delete': true,
  'projects.updateProgress': true,
  // Customers - full access
  'customers.view': true,
  'customers.create': true,
  'customers.edit': true,
  // Quotes - full access
  'quotes.view': true,
  'quotes.viewAll': true,
  'quotes.create': true,
  'quotes.edit': true,
  'quotes.manageTemplates': true,
  // Invoices - full access
  'invoices.view': true,
  'invoices.create': true,
  'invoices.edit': true,
  'invoices.book': true,
  'invoices.manageTemplates': true,
  // Materials - full access
  'materials.view': true,
  'materials.create': true,
  'materials.edit': true,
  // Creditor invoices - full access
  'creditorInvoices.view': true,
  'creditorInvoices.book': true,
  // Timeplans - full access
  'timeplans.view': true,
  'timeplans.manage': true,
  // Activity feed - full access
  'activity.viewAll': true,
  // Planning - full access
  'planning.view': true,
  'planning.viewAll': true,
  'planning.edit': true,
  // Users - full access
  'users.view': true,
  'users.create': true,
  'users.edit': true,
  'users.viewSelf': true,
  // Settings - view, edit, and manage permissions (but UI restricts admin tab to owners only)
  'settings.view': true,
  'settings.editCompany': true,
  'settings.manageMembers': true,
  'settings.managePermissions': true,
  'settings.dangerZone': false,
  // Reports - full access
  'reports.view': true,
  'insights.view': true,
  // Time entries - full access
  'timeEntries.view': true,
  'timeEntries.create': true,
  'timeEntries.viewAll': true,
  'timeEntries.approve': true,
  'timeEntries.editOthers': true,
  // Budgettering - full access
  'budgettering.view': true,
  'budgettering.viewAllBudgets': true,
  'budgettering.viewTimeOverview': true,
  'budgettering.edit': true,
  'budgettering.recordStade': true,
  // Quality - full access
  'quality.view': true,
  // KLS - full access
  'kls.view': true,
  'kls.createInspections': true,
  'kls.manageChecklists': true,
  // Bonus - full access
  'bonus.view': true,
  'bonus.viewAll': true,
  'bonus.manage': true,
  // Workspace - full access
  'workspace.create': true,
  'workspace.manageParticipants': true,
  'workspace.decideOutcome': true,
  'workspace.overrideBillable': true,
  // Price Library & Calculations - full access
  'priceLibrary.view': true,
  'priceLibrary.edit': true,
  'calculations.view': true,
  'calculations.edit': true,
  'calculations.generateQuote': true,
  // Partners - full access
  'partners.invite': true,
  'partners.view': true,
  'partners.manage': true,
  // Defects - full access
  'defects.view': true,
  'defects.create': true,
  'defects.edit': true,
  'defects.delete': true,
  // Aftalesedler - full access
  'aftalesedler.create': true,
  'aftalesedler.view': true,
  'aftalesedler.edit': true,
  'aftalesedler.approve': true,
  // Tilbudslister - full access
  'tilbudslister.view': true,
  'tilbudslister.manage': true,
  // Integrations - admin has full access
  'integrations.economic.use': true,
  'integrations.dinero.use': true,
  'integrations.payroll.manage': true,
  'integrations.connections.manage': true,
  // Assets - admin has full access
  'assets.view': true,
  'assets.edit': true,
  // Ejendomme - full access
  'properties.view': true,
  'properties.edit': true,
  'properties.viewAccessCodes': true,
  // KS Templates - admin has full access
  'ksTemplates.view': true,
  'ksTemplates.edit': true,
  // Verification Templates - admin has full access
  'verificationTemplates.view': true,
  'verificationTemplates.edit': true,
  // KS Project Items - admin has full access
  'ksProjectItems.view': true,
  'ksProjectItems.edit': true,
  // KLS Checklists - admin has full access
  'klsChecklists.view': true,
  'klsChecklists.edit': true,
  // KLS Equipment - admin has full access
  'klsEquipment.view': true,
  'klsEquipment.edit': true,
  // KLS Evaluations - admin has full access
  'klsEvaluations.view': true,
  'klsEvaluations.edit': true,
  // KLS Inspections - admin has full access
  'klsInspections.view': true,
  'klsInspections.edit': true,
  // KLS Competencies - admin has full access
  'klsCompetencies.view': true,
  'klsCompetencies.edit': true,
  // KLS Deviations - admin has full access
  'klsDeviations.view': true,
  'klsDeviations.edit': true,
  // Documents - admin has full access
  'documents.view': true,
  'documents.upload': true,
  'documents.edit': true,
  'documents.delete': true,
};

/**
 * Default permissions for project managers.
 * Project managers have elevated permissions - can see/manage projects, quotes, planning.
 */
export const DEFAULT_PROJECT_MANAGER_PERMISSIONS: Record<PermissionKey, boolean> = {
  // Dashboard - full access
  'dashboard.view': true,
  'dashboard.viewAllStats': true,
  // Projects - full access
  'projects.view': true,
  'projects.viewAll': true,
  'projects.create': true,
  'projects.edit': true,
  'projects.delete': false,
  'projects.updateProgress': true,
  // Customers - view and edit
  'customers.view': true,
  'customers.create': true,
  'customers.edit': true,
  // Quotes - full access
  'quotes.view': true,
  'quotes.viewAll': true,
  'quotes.create': true,
  'quotes.edit': true,
  'quotes.manageTemplates': true,
  // Invoices - view, create, edit (book is admin-only by default)
  'invoices.view': true,
  'invoices.create': true,
  'invoices.edit': true,
  'invoices.book': true,
  'invoices.manageTemplates': true,
  // Materials - full access
  'materials.view': true,
  'materials.create': true,
  'materials.edit': true,
  // Creditor invoices - view + book (PMs handle supplier invoices)
  'creditorInvoices.view': true,
  'creditorInvoices.book': true,
  // Timeplans - full access
  'timeplans.view': true,
  'timeplans.manage': true,
  // Activity feed - full access
  'activity.viewAll': true,
  // Planning - full access
  'planning.view': true,
  'planning.viewAll': true,
  'planning.edit': true,
  // Users - view only
  'users.view': true,
  'users.create': false,
  'users.edit': false,
  'users.viewSelf': true,
  // Settings - no access
  'settings.view': false,
  'settings.editCompany': false,
  'settings.manageMembers': false,
  'settings.managePermissions': false,
  'settings.dangerZone': false,
  // Reports - full access
  'reports.view': true,
  'insights.view': true,
  // Time entries - full access
  'timeEntries.view': true,
  'timeEntries.create': true,
  'timeEntries.viewAll': true,
  'timeEntries.approve': true,
  'timeEntries.editOthers': false,
  // Budgettering - full access
  'budgettering.view': true,
  'budgettering.viewAllBudgets': true,
  'budgettering.viewTimeOverview': true,
  'budgettering.edit': false,
  'budgettering.recordStade': false,
  // Quality - full access
  'quality.view': true,
  // KLS - full access
  'kls.view': true,
  'kls.createInspections': true,
  'kls.manageChecklists': true,
  // Bonus - view all but not manage
  'bonus.view': true,
  'bonus.viewAll': true,
  'bonus.manage': false,
  // Workspace - full access except override billable
  'workspace.create': true,
  'workspace.manageParticipants': true,
  'workspace.decideOutcome': true,
  'workspace.overrideBillable': false,
  // Price Library & Calculations - full access
  'priceLibrary.view': true,
  'priceLibrary.edit': true,
  'calculations.view': true,
  'calculations.edit': true,
  'calculations.generateQuote': true,
  // Partners - full access
  'partners.invite': true,
  'partners.view': true,
  'partners.manage': true,
  // Defects - full access except delete
  'defects.view': true,
  'defects.create': true,
  'defects.edit': true,
  'defects.delete': false,
  // Aftalesedler - full access
  'aftalesedler.create': true,
  'aftalesedler.view': true,
  'aftalesedler.edit': true,
  'aftalesedler.approve': true,
  // Tilbudslister - full access
  'tilbudslister.view': true,
  'tilbudslister.manage': true,
  // Integrations - project managers don't use e-conomic by default
  'integrations.economic.use': false,
  'integrations.dinero.use': false,
  'integrations.payroll.manage': false,
  'integrations.connections.manage': false,
  // Assets - project managers can view and edit
  'assets.view': true,
  'assets.edit': true,
  // Ejendomme - full access
  'properties.view': true,
  'properties.edit': true,
  'properties.viewAccessCodes': true,
  // KS Templates - project managers can view and edit
  'ksTemplates.view': true,
  'ksTemplates.edit': true,
  // Verification Templates - project managers can view and edit
  'verificationTemplates.view': true,
  'verificationTemplates.edit': true,
  // KS Project Items - project managers can view and edit
  'ksProjectItems.view': true,
  'ksProjectItems.edit': true,
  // KLS Checklists - project managers can view and edit
  'klsChecklists.view': true,
  'klsChecklists.edit': true,
  // KLS Equipment - project managers can view and edit
  'klsEquipment.view': true,
  'klsEquipment.edit': true,
  // KLS Evaluations - project managers can view and edit
  'klsEvaluations.view': true,
  'klsEvaluations.edit': true,
  // KLS Inspections - project managers can view and edit
  'klsInspections.view': true,
  'klsInspections.edit': true,
  // KLS Competencies - project managers can view and edit
  'klsCompetencies.view': true,
  'klsCompetencies.edit': true,
  // KLS Deviations - project managers can view and edit
  'klsDeviations.view': true,
  'klsDeviations.edit': true,
  // Documents - project managers can view, upload, edit and delete
  'documents.view': true,
  'documents.upload': true,
  'documents.edit': true,
  'documents.delete': true,
};

/**
 * Default permissions for administration team.
 * Administration can handle customers, quotes, invoices, but not technical aspects.
 */
export const DEFAULT_ADMINISTRATION_PERMISSIONS: Record<PermissionKey, boolean> = {
  // Dashboard - full access
  'dashboard.view': true,
  'dashboard.viewAllStats': true,
  // Projects - view all but limited editing
  'projects.view': true,
  'projects.viewAll': true,
  'projects.create': false,
  'projects.edit': false,
  'projects.delete': false,
  'projects.updateProgress': false,
  // Customers - full access
  'customers.view': true,
  'customers.create': true,
  'customers.edit': true,
  // Quotes - full access
  'quotes.view': true,
  'quotes.viewAll': true,
  'quotes.create': true,
  'quotes.edit': true,
  'quotes.manageTemplates': true,
  // Invoices - full access (administration handles billing)
  'invoices.view': true,
  'invoices.create': true,
  'invoices.edit': true,
  'invoices.book': true,
  'invoices.manageTemplates': true,
  // Materials - view + edit (admin handles cost allocation)
  'materials.view': true,
  'materials.create': false,
  'materials.edit': true,
  // Creditor invoices - full access (administration books supplier invoices)
  'creditorInvoices.view': true,
  'creditorInvoices.book': true,
  // Timeplans - view only (PM owns the schedule)
  'timeplans.view': true,
  'timeplans.manage': false,
  // Activity feed - full access (compliance / audit role)
  'activity.viewAll': true,
  // Planning - view all but no edit
  'planning.view': true,
  'planning.viewAll': true,
  'planning.edit': false,
  // Users - view only
  'users.view': true,
  'users.create': false,
  'users.edit': false,
  'users.viewSelf': true,
  // Settings - no access
  'settings.view': false,
  'settings.editCompany': false,
  'settings.manageMembers': false,
  'settings.managePermissions': false,
  'settings.dangerZone': false,
  // Reports - full access
  'reports.view': true,
  'insights.view': true,
  // Time entries - view all
  'timeEntries.view': true,
  'timeEntries.create': true,
  'timeEntries.viewAll': true,
  'timeEntries.approve': false,
  'timeEntries.editOthers': true,
  // Budgettering - view all
  'budgettering.view': true,
  'budgettering.viewAllBudgets': true,
  'budgettering.viewTimeOverview': true,
  'budgettering.edit': false,
  'budgettering.recordStade': false,
  // Quality - view access
  'quality.view': true,
  // KLS - view only
  'kls.view': true,
  'kls.createInspections': false,
  'kls.manageChecklists': false,
  // Bonus - view all (administration role typically handles payroll)
  'bonus.view': true,
  'bonus.viewAll': true,
  'bonus.manage': false,
  // Workspace - can create and manage but not decide outcome
  'workspace.create': true,
  'workspace.manageParticipants': true,
  'workspace.decideOutcome': false,
  'workspace.overrideBillable': false,
  // Price Library & Calculations - view only
  'priceLibrary.view': true,
  'priceLibrary.edit': false,
  'calculations.view': true,
  'calculations.edit': false,
  'calculations.generateQuote': false,
  // Partners - no access
  'partners.invite': false,
  'partners.view': false,
  'partners.manage': false,
  // Defects - view only
  'defects.view': true,
  'defects.create': false,
  'defects.edit': false,
  'defects.delete': false,
  // Aftalesedler - view only (administration can see, PMs manage)
  'aftalesedler.create': false,
  'aftalesedler.view': true,
  'aftalesedler.edit': false,
  'aftalesedler.approve': false,
  // Tilbudslister - view only
  'tilbudslister.view': true,
  'tilbudslister.manage': false,
  // Integrations - administration has access
  'integrations.economic.use': true,
  'integrations.dinero.use': true,
  'integrations.payroll.manage': true,
  'integrations.connections.manage': false,
  // Assets - administration can view
  'assets.view': true,
  'assets.edit': false,
  // Ejendomme - full access
  'properties.view': true,
  'properties.edit': true,
  'properties.viewAccessCodes': true,
  // KS Templates - administration can view
  'ksTemplates.view': true,
  'ksTemplates.edit': false,
  // Verification Templates - administration can view
  'verificationTemplates.view': true,
  'verificationTemplates.edit': false,
  // KS Project Items - administration can view
  'ksProjectItems.view': true,
  'ksProjectItems.edit': false,
  // KLS Checklists - administration can view
  'klsChecklists.view': true,
  'klsChecklists.edit': false,
  // KLS Equipment - administration can view
  'klsEquipment.view': true,
  'klsEquipment.edit': false,
  // KLS Evaluations - administration can view
  'klsEvaluations.view': true,
  'klsEvaluations.edit': false,
  // KLS Inspections - administration can view
  'klsInspections.view': true,
  'klsInspections.edit': false,
  // KLS Competencies - administration can view
  'klsCompetencies.view': true,
  'klsCompetencies.edit': false,
  // KLS Deviations - administration can view
  'klsDeviations.view': true,
  'klsDeviations.edit': false,
  // Documents - administration can view, upload and edit, but not delete
  'documents.view': true,
  'documents.upload': true,
  'documents.edit': true,
  'documents.delete': false,
};

/**
 * Default permissions for trade workers (carpenter, plumber, electrician, etc.).
 * Limited to their own tasks, time registration, and KLS inspections.
 */
export const DEFAULT_TRADEWORKER_PERMISSIONS: Record<PermissionKey, boolean> = {
  // Dashboard - view only, not all stats
  'dashboard.view': true,
  'dashboard.viewAllStats': false,
  // Projects - view assigned only
  'projects.view': true,
  'projects.viewAll': false,
  'projects.create': false,
  'projects.edit': false,
  'projects.delete': false,
  'projects.updateProgress': false,
  // Customers - read-only (workers see the customer of the job they're on)
  'customers.view': true,
  'customers.create': false,
  'customers.edit': false,
  // Quotes - no access
  'quotes.view': false,
  'quotes.viewAll': false,
  'quotes.create': false,
  'quotes.edit': false,
  'quotes.manageTemplates': false,
  // Invoices - no access
  'invoices.view': false,
  'invoices.create': false,
  'invoices.edit': false,
  'invoices.book': false,
  'invoices.manageTemplates': false,
  // Materials - view + create on assigned projects (workers record what they used)
  'materials.view': true,
  'materials.create': true,
  'materials.edit': false,
  // Creditor invoices - no access
  'creditorInvoices.view': false,
  'creditorInvoices.book': false,
  // Timeplans - view (workers see when they're scheduled)
  'timeplans.view': true,
  'timeplans.manage': false,
  // Activity feed - no access
  'activity.viewAll': false,
  // Planning - view own schedule only
  'planning.view': true,
  'planning.viewAll': false,
  'planning.edit': false,
  // Users - no access
  'users.view': false,
  'users.create': false,
  'users.edit': false,
  'users.viewSelf': true,
  // Settings - no access
  'settings.view': false,
  'settings.editCompany': false,
  'settings.manageMembers': false,
  'settings.managePermissions': false,
  'settings.dangerZone': false,
  // Reports - no access
  'reports.view': false,
  'insights.view': false,
  // Time entries - own only
  'timeEntries.view': true,
  'timeEntries.create': true,
  'timeEntries.viewAll': false,
  'timeEntries.approve': false,
  'timeEntries.editOthers': false,
  // Budgettering - time overview only (not budget/economy)
  'budgettering.view': false,
  'budgettering.viewAllBudgets': false,
  'budgettering.viewTimeOverview': true,
  'budgettering.edit': false,
  'budgettering.recordStade': false,
  // Quality - view access
  'quality.view': true,
  // KLS - view and create inspections, but not manage checklists
  'kls.view': true,
  'kls.createInspections': true,
  'kls.manageChecklists': false,
  // Bonus - view own only
  'bonus.view': true,
  'bonus.viewAll': false,
  'bonus.manage': false,
  // Workspace - no access (trade workers are invited as participants)
  'workspace.create': false,
  'workspace.manageParticipants': false,
  'workspace.decideOutcome': false,
  'workspace.overrideBillable': false,
  // Price Library & Calculations - no access
  'priceLibrary.view': false,
  'priceLibrary.edit': false,
  'calculations.view': false,
  'calculations.edit': false,
  'calculations.generateQuote': false,
  // Partners - no access
  'partners.invite': false,
  'partners.view': false,
  'partners.manage': false,
  // Defects - trade workers view, log and edit defects on site (not delete — destructive stays manager-only)
  'defects.view': true,
  'defects.create': true,
  'defects.edit': true,
  'defects.delete': false,
  // Aftalesedler - no access (trade workers don't manage supplements)
  'aftalesedler.create': false,
  'aftalesedler.view': false,
  'aftalesedler.edit': false,
  'aftalesedler.approve': false,
  // Tilbudslister - no access
  'tilbudslister.view': false,
  'tilbudslister.manage': false,
  // Integrations - no access
  'integrations.economic.use': false,
  'integrations.dinero.use': false,
  'integrations.payroll.manage': false,
  'integrations.connections.manage': false,
  // Assets - trade workers can view assets
  'assets.view': true,
  'assets.edit': false,
  // Ejendomme - read-only, but the access codes are included on purpose: a worker who
  // cannot read the key box code has to ring the office from the doorstep.
  'properties.view': true,
  'properties.edit': false,
  'properties.viewAccessCodes': true,
  // KS Templates - trade workers can view
  'ksTemplates.view': true,
  'ksTemplates.edit': false,
  // Verification Templates - trade workers can view
  'verificationTemplates.view': true,
  'verificationTemplates.edit': false,
  // KS Project Items - trade workers can view and edit (they fill in checklist items on site)
  'ksProjectItems.view': true,
  'ksProjectItems.edit': true,
  // KLS Checklists - trade workers can view
  'klsChecklists.view': true,
  'klsChecklists.edit': false,
  // KLS Equipment - trade workers can view
  'klsEquipment.view': true,
  'klsEquipment.edit': false,
  // KLS Evaluations - trade workers can view
  'klsEvaluations.view': true,
  'klsEvaluations.edit': false,
  // KLS Inspections - trade workers can view and edit (they fill in inspections on site)
  'klsInspections.view': true,
  'klsInspections.edit': true,
  // KLS Competencies - trade workers can view their own profile
  'klsCompetencies.view': true,
  'klsCompetencies.edit': false,
  // KLS Deviations - trade workers can view and report deviations
  'klsDeviations.view': true,
  'klsDeviations.edit': true,
  // Documents - trade workers can view, upload and edit, but not delete
  'documents.view': true,
  'documents.upload': true,
  'documents.edit': true,
  'documents.delete': false,
};

/**
 * The permission baseline for each team kind — the client mirror of the server's
 * `PermissionDefaults.ForKind`.
 *
 * No grant changes from the twelve-trade model: `Office` is byte-for-byte the old
 * Administration set and `Field` byte-for-byte the old TradeWorker set. The elevated
 * project-manager set is no longer any kind's baseline; it survives only as a creation
 * template, in {@link DEFAULT_TEMPLATE_PERMISSIONS}.
 *
 * This is a *fallback*, used while `GET /api/users/me` has not answered yet. The server's
 * resolved set is always authoritative.
 */
export const DEFAULT_KIND_PERMISSIONS: Record<TeamKind, Record<PermissionKey, boolean>> = {
  Office: DEFAULT_ADMINISTRATION_PERMISSIONS,
  Field: DEFAULT_TRADEWORKER_PERMISSIONS,
};

/**
 * The set each creation template starts a team on. The server writes a persisted row only when
 * the chosen template differs from the kind's own baseline, so a team on `Kontor`/`Office` or
 * `Mark`/`Field` keeps tracking the live defaults and picks up permission keys shipped later.
 */
export const DEFAULT_TEMPLATE_PERMISSIONS: Record<TeamPermissionTemplate, Record<PermissionKey, boolean>> = {
  Kontor: DEFAULT_ADMINISTRATION_PERMISSIONS,
  Projektledelse: DEFAULT_PROJECT_MANAGER_PERMISSIONS,
  Mark: DEFAULT_TRADEWORKER_PERMISSIONS,
};

/**
 * User permission override document.
 * Stored in subcollection: COMPANIES/{companyId}/USER_PERMISSION_OVERRIDES/{userId}
 *
 * This pattern provides better cache invalidation and simpler data management
 * compared to deeply nested data in the company document.
 */
export interface UserPermissionOverrideModel {
  /** Document ID - same as the user's UID */
  id: string;
  /** Flat map of permission keys to override values */
  permissions: Partial<Record<PermissionKey, boolean>>;
  /** When the overrides were last updated */
  updatedAt: string;
  /** UID of the user who last updated the overrides */
  updatedBy: string;
}

/**
 * Company-level permission configuration.
 * Stored in CompanySettingsModel.permissions
 */
export interface PermissionConfig {
  /**
   * Admin permission overrides.
   * Admins have their own configurable permissions separate from teams.
   */
  adminPermissions?: Partial<Record<PermissionKey, boolean>>;
  /**
   * Team-level permission overrides, keyed by the team's id (`teams.id`, a guid string).
   * A team absent here uses {@link DEFAULT_KIND_PERMISSIONS} for its kind, which is what keeps
   * it tracking permission keys shipped after it was created.
   */
  teamPermissions?: Record<string, Partial<Record<PermissionKey, boolean>>>;
  /**
   * Per-user permission overrides.
   * Key is user UID, value is permission overrides.
   * These extend or restrict the user's role/team-based permissions.
   */
  userOverrides?: Record<string, Partial<Record<PermissionKey, boolean>>>;
  /** When the permission config was last updated */
  updatedAt?: string;
  /** UID of the user who last updated permissions */
  updatedBy?: string;
}

/**
 * Default permissions for a company role. Teams no longer come into it: a team's baseline is
 * {@link DEFAULT_KIND_PERMISSIONS}, keyed by its kind, and {@link resolvePermissionsForTeam} is
 * the entry point for a user.
 */
export function getDefaultPermissions(role: UserMembershipTypeModel): Record<PermissionKey, boolean> {
  switch (role) {
    case 'owner':
      // Owner has all permissions
      return ALL_PERMISSIONS.reduce((acc, p) => ({ ...acc, [p.key]: true }), {} as Record<PermissionKey, boolean>);
    case 'admin':
      return { ...DEFAULT_ADMIN_PERMISSIONS };
    case 'user':
      // A user with no team resolves to the field baseline, exactly as the server does.
      return { ...DEFAULT_KIND_PERMISSIONS.Field };
    default:
      // Unknown role gets no permissions
      return ALL_PERMISSIONS.reduce((acc, p) => ({ ...acc, [p.key]: false }), {} as Record<PermissionKey, boolean>);
  }
}

/**
 * Resolve effective permissions for a user from their team record.
 *
 * The team-shaped counterpart of {@link resolvePermissions}: the baseline comes from
 * `team.kind` rather than a fixed trade, and the company override is looked up by `team.id`.
 * A null team resolves to the field baseline, matching the server's null-team rule exactly.
 *
 * This is the client-side fallback for the window before `GET /api/users/me` answers. The
 * server's resolved set always wins once it arrives.
 */
export function resolvePermissionsForTeam(
  role: UserMembershipTypeModel,
  team?: TeamSummary | null,
  permissionConfig?: PermissionConfig | null,
  userId?: string,
  userOverrides?: Partial<Record<PermissionKey, boolean>> | null,
): Record<PermissionKey, boolean> {
  if (role === 'owner') {
    return getDefaultPermissions('owner');
  }

  const base: Record<PermissionKey, boolean> =
    role === 'admin'
      ? { ...DEFAULT_ADMIN_PERMISSIONS }
      : { ...DEFAULT_KIND_PERMISSIONS[team?.kind ?? 'Field'] };

  const resolved = { ...base };
  if (!permissionConfig) {
    return resolved;
  }

  const applyOverrides = (overrides: Record<string, unknown>) => {
    for (const [category, categoryValue] of Object.entries(overrides)) {
      if (typeof categoryValue === 'boolean') {
        resolved[category as PermissionKey] = categoryValue;
      } else if (categoryValue && typeof categoryValue === 'object') {
        for (const [action, actionValue] of Object.entries(categoryValue as Record<string, boolean>)) {
          if (typeof actionValue === 'boolean') {
            resolved[`${category}.${action}` as PermissionKey] = actionValue;
          }
        }
      }
    }
  };

  const teamOverrides = team ? permissionConfig.teamPermissions?.[team.id] : undefined;

  if (role === 'admin' && permissionConfig.adminPermissions) {
    applyOverrides(permissionConfig.adminPermissions);
  } else if (role === 'user' && teamOverrides) {
    applyOverrides(teamOverrides);
  }

  if (userId && permissionConfig.userOverrides?.[userId]) {
    applyOverrides(permissionConfig.userOverrides[userId]);
  }

  if (userOverrides) {
    for (const [key, value] of Object.entries(userOverrides)) {
      if (typeof value === 'boolean') {
        resolved[key as PermissionKey] = value;
      }
    }
  }

  return resolved;
}

/**
 * Get permissions grouped by category for UI display.
 */
export function getPermissionsByCategory(): Map<PermissionCategory, PermissionMetadata[]> {
  const byCategory = new Map<PermissionCategory, PermissionMetadata[]>();

  for (const category of PERMISSION_CATEGORIES) {
    byCategory.set(category.key, []);
  }

  for (const permission of ALL_PERMISSIONS) {
    const existing = byCategory.get(permission.category) ?? [];
    existing.push(permission);
    byCategory.set(permission.category, existing);
  }

  return byCategory;
}
